import React, { Fragment, useState, useCallback, useEffect } from 'react';
import {
  Create,
  CreateBase,
  CreateProps,
  required,
  ShowBase,
  Show,
  SimpleForm,
  useTranslate,
  TextField,
  DateField,
  SimpleShowLayout,
  TopToolbar,
  EditButton,
  useCreate,
  useRecordContext,
  ReferenceField,
  BooleanField,
  NumberField,
  Labeled,
  RaRecord,
  TextFieldProps,
  SingleFieldList,
  SelectField,
  RichTextField,
  FunctionField,
  RecordContextProvider,
  useDataProvider,
} from 'react-admin';
import { EditInDialogButton } from '@react-admin/ra-form-layout';
import {
  Grid,
  Box,
  Typography,
  Stack,
  Chip,
  useMediaQuery,
  Card,
  Divider,
  IconButton,
  Theme,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FuneralShowToolBar from './FuneralShowToolBar';
import FuneralLinkedContractsList from './FuneralLinkedContractsList';
import FuneralLinkedClientsList from './FuneralLinkedClientsList';
import { Funeral, ReviewRequest } from '../types';
import FuneralFileUploader from './FuneralFileUploader';
import UserProfileField from '../components/UserProfileField';
import { FuneralBalanceSummary } from './FuneralBalanceSummary';
import { FuneralCasket } from './FuneralCasket';
import moment from 'moment';
import { ClientSaveToolbar } from '../clients/ClientSaveToolbar';
import ClientEditBase from '../clients/ClientEditBase';
import CremationTracker from '../components/CremationTracker';
import { SP3DataViewer } from './SP3DataViewer';
import { SP3PdfParser } from './SP3PdfParser';
import ValidationStatusIndicator from './ValidationStatusIndicator';
import { CustomBooleanField } from '../components/CustomBooleanField';
import { DecStatusField } from '../components/DecStatusField';
import { BenefitsStatusField } from './BenefitsStatusField';
import { DeliveryEditButton } from './components/DeliveryEditDialog';
import EditIcon from '@mui/icons-material/Edit';
import { DeliveryAddress } from '../types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNotify } from 'react-admin';
import { FuneralShowRedesign } from './FuneralShowRedesign';
import { FuneralShowSidebar } from './FuneralShowSidebar';
import { MobileFuneralShowRedesign } from './MobileFuneralShowRedesign';
import { MessageDraftProvider } from './contexts/MessageDraftContext';

// Aircall Button Component
const AircallButton = ({ phoneNumber }: { phoneNumber: string }) => {
  if (!phoneNumber) return null;
  
  const handleAircallClick = () => {
    window.open(`aircall:${phoneNumber}`, '_self');
  };

  return (
    <IconButton
      onClick={handleAircallClick}
      size="small"
      sx={{ 
        ml: 1,
        '& img': {
          width: 20,
          height: 20,
        }
      }}
      title={`Call ${phoneNumber}`}
    >
      <img src="/aircall.png" alt="Call" />
    </IconButton>
  );
};

// Copy App Link Button Component
const CopyAppLinkButton = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (!record?.uuid) return null;

  const buildAppLink = () => {
    const fallback = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3010' : 'https://cleocremation.com';
    const auraAppUrl = (process.env.REACT_APP_AURA_APP_URL || fallback).replace(/\/$/, '');
    return `${auraAppUrl}/app/${record.uuid}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildAppLink());
      notify('App link copied to clipboard!', { type: 'success' });
    } catch (error) {
      notify('Failed to copy link', { type: 'error' });
    }
  };

  // Open the client link with ?staff=1 so aura-app suppresses the page-view
  // timeline events while staff is previewing.
  const handlePreviewLink = () => {
    setMenuAnchor(null);
    window.open(`${buildAppLink()}?staff=1`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <IconButton
        onClick={handleCopyLink}
        size="small"
        sx={{ ml: 1 }}
        title="Copy app link"
      >
        <ContentCopyIcon />
      </IconButton>
      <IconButton
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        size="small"
        title="More link options"
        sx={{ p: 0 }}
      >
        <ArrowDropDownIcon />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handlePreviewLink}>Preview Link</MenuItem>
      </Menu>
    </>
  );
};

// Region Chip Component
const RegionChip = () => {
  const record = useRecordContext<Funeral>();
  
  if (!record?.region) return null;

  const isQuebec = record.region === 'quebec';
  const regionLabel = isQuebec ? 'Quebec' : 'Ontario';
  const bgColor = isQuebec ? '#4169E1' : '#DC143C'; // Quebec: Royal Blue, Ontario: Crimson

  return (
    <Chip 
      label={regionLabel}
      sx={{ 
        backgroundColor: bgColor,
        color: 'white',
        fontWeight: 600,
        fontSize: '0.9rem',
        height: '32px',
        '& .MuiChip-label': {
          px: 2,
        }
      }}
    />
  );
};

// Review Request Status Component
const ReviewRequestStatus = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchReviewRequest = async () => {
      if (!record?.id) return;
      
      try {
        const response = await dataProvider.getList('review_requests', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'created_at', order: 'DESC' },
          filter: { funeral_id: record.id },
        });
        
        if (response.data && response.data.length > 0) {
          setReviewRequest(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching review request:', error);
      } finally {
        setLoading(false);
      }
    };
    
    void fetchReviewRequest();
  }, [dataProvider, record?.id]);

  const handleToggle = async () => {
    if (!reviewRequest?.id) return;
    
    setUpdating(true);
    try {
      await dataProvider.update('review_requests', {
        id: reviewRequest.id,
        data: { enabled: !reviewRequest.enabled },
        previousData: reviewRequest,
      });
      
      setReviewRequest(prev => prev ? { ...prev, enabled: !prev.enabled } : null);
      notify(reviewRequest.enabled ? 'Review request disabled' : 'Review request enabled', { type: 'success' });
    } catch (error) {
      console.error('Error updating review request:', error);
      notify('Failed to update review request', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!reviewRequest) {
    return null; // No review request scheduled
  }

  const isSent = !!reviewRequest.sent_at;
  const scheduledDate = new Date(reviewRequest.scheduled_date);
  const sentDate = reviewRequest.sent_at ? new Date(reviewRequest.sent_at) : null;

  const getSendMethodLabel = (method: string) => {
    switch (method) {
      case 'sms': return 'SMS';
      case 'email': return 'Email';
      case 'both': return 'SMS & Email';
      default: return method;
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            border: '2px solid',
            borderColor: isSent ? 'success.main' : reviewRequest.enabled ? 'info.main' : 'grey.400',
            bgcolor: isSent ? 'success.main' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isSent ? (
            <CheckCircleIcon sx={{ color: 'white', fontSize: 14 }} />
          ) : reviewRequest.enabled ? (
            <ScheduleIcon sx={{ color: 'info.main', fontSize: 14 }} />
          ) : null}
        </Box>
        <RateReviewIcon sx={{ color: isSent ? 'success.main' : reviewRequest.enabled ? 'info.main' : 'grey.500', fontSize: 20 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Review Request:
        </Typography>
      </Stack>
      
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        {isSent ? (
          <Typography variant="body2" color="success.main" fontWeight={500}>
            Sent on {sentDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} via {getSendMethodLabel(reviewRequest.send_method)}
          </Typography>
        ) : reviewRequest.enabled ? (
          <>
            <Typography variant="body2">
              <strong>Scheduled:</strong> {scheduledDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Typography>
            <Typography variant="body2">
              <strong>Method:</strong> {getSendMethodLabel(reviewRequest.send_method)}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Disabled (was scheduled for {scheduledDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
          </Typography>
        )}
      </Stack>

      {!isSent && (
        <Tooltip title={reviewRequest.enabled ? 'Click to disable review request' : 'Click to enable review request'}>
          <Switch
            checked={reviewRequest.enabled}
            onChange={handleToggle}
            disabled={updating}
            size="small"
            color="primary"
          />
        </Tooltip>
      )}
      
      {updating && <CircularProgress size={16} />}
    </Stack>
  );
};

// Partner Visibility Toggle Component
const PartnerVisibilityToggle = () => {
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [visible, setVisible] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (record) {
      setVisible(record.visible_to_partners !== false);
    }
  }, [record?.visible_to_partners]);

  if (!record) return null;

  const handleToggle = async () => {
    const newValue = !visible;
    setVisible(newValue);
    setUpdating(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: { visible_to_partners: newValue },
        previousData: record,
      });
      notify(newValue ? 'Case visible to partners' : 'Case hidden from partners', { type: 'success' });
    } catch (error) {
      console.error('Error updating partner visibility:', error);
      setVisible(!newValue);
      notify('Failed to update partner visibility', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Visible to Partners
      </Typography>
      <Tooltip title={visible ? 'Click to hide from partner portal' : 'Click to show on partner portal'}>
        <Switch
          checked={visible}
          onChange={handleToggle}
          disabled={updating}
          size="small"
          color="primary"
        />
      </Tooltip>
      {updating && <CircularProgress size={16} />}
    </Stack>
  );
};

// Delivery Plan Status Component
const DeliveryPlanStatus = () => {
  const record = useRecordContext();
  
  if (!record) return null;
  
  const delivery = record.delivery_address;
  const hasDeliveryDay = record.delivery_day;
  const hasDeliveryWindow = record.delivery_window;
  // Same rule as the redesigned card: the confirmation stamp decides, and the
  // day is only a fallback for cases that predate that column. Keying on the
  // day alone hid every Canada Post confirmation, which never has one.
  const isConfirmed =
    record.delivery_confirmed_at ||
    (delivery?.address &&
      (hasDeliveryDay || hasDeliveryWindow === 'xpresspost_signature'));
  
  // Check if it's Montreal based on postal code
  const isMontreal = delivery?.postal_code?.trim().toUpperCase().startsWith('H');
  
  // Format address
  const formatAddress = () => {
    if (!delivery?.address && !delivery?.civic_number) return 'Not set';
    const parts = [];
    
    // Format street address (apartment-civic_number address format)
    const streetParts = [];
    if (delivery.apartment && delivery.civic_number) {
      streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
    } else if (delivery.apartment) {
      streetParts.push(delivery.apartment);
    } else if (delivery.civic_number) {
      streetParts.push(delivery.civic_number);
    }
    if (delivery.address) {
      streetParts.push(delivery.address);
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(' '));
    }
    
    if (delivery.city) parts.push(delivery.city);
    if (delivery.province) parts.push(delivery.province);
    if (delivery.postal_code) parts.push(delivery.postal_code);
    return parts.join(', ');
  };
  
  // Format delivery time
  const formatDeliveryTime = () => {
    if (!isConfirmed) return null;
    
    if (isMontreal && hasDeliveryDay && hasDeliveryWindow) {
      const date = new Date(hasDeliveryDay);
      const formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      return `${formattedDate} between ${hasDeliveryWindow}`;
    } else if (!isMontreal && delivery?.delivery_window === 'xpresspost_signature') {
      return 'Canada Xpresspost with Signature';
    }
    
    return null;
  };
  
  const deliveryTime = formatDeliveryTime();
  
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            border: '2px solid',
            borderColor: isConfirmed ? 'success.main' : 'grey.400',
            bgcolor: isConfirmed ? 'success.main' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isConfirmed && (
            <Box
              component="span"
              sx={{
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                lineHeight: 1,
              }}
            >
              ✓
            </Box>
          )}
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Delivery Plan:
        </Typography>
      </Stack>
      
      <Stack spacing={0.5}>
        <Typography variant="body2">
          <strong>Address:</strong> {formatAddress()}
        </Typography>
        {deliveryTime && (
          <Typography variant="body2">
            <strong>{isMontreal ? 'Time:' : 'Method:'}</strong> {deliveryTime}
          </Typography>
        )}
        {!isConfirmed && (
          <Typography variant="body2" color="warning.main">
            Not yet confirmed
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const FuneralShow = () => {
  const isXSmall = useMediaQuery<Theme>(theme =>
    theme.breakpoints.down('sm')
  );

  return (
    <MessageDraftProvider>
    <Show
      actions={isXSmall ? false : <FuneralShowToolBar />}
      aside={
        !isXSmall ? (
          <div
            className="cleo-ui"
            style={{
              height: 'calc(100vh - 128px)',
              borderRadius: '8px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <FuneralShowSidebar />
          </div>
        ) : undefined
      }
      sx={
        isXSmall
          ? {
              '& .RaShow-main': { maxWidth: '100%', display: 'flex', padding: 0 },
              '& .RaShow-card': {
                boxShadow: 'none',
                borderRadius: 0,
                overflow: 'hidden',
                flex: 1,
                border: 'none',
                height: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .RaShow-card > .MuiCardContent-root': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: 0,
                '&:last-child': { paddingBottom: 0 },
              },
            }
          : {
              '& .RaShow-main': { maxWidth: '100%', display: 'flex', gap: '16px', padding: '16px 16px 16px 0' },
              '& .RaShow-card': {
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                borderRadius: '8px',
                overflow: 'hidden',
                flex: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                height: 'calc(100vh - 128px)',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .RaShow-card > .MuiCardContent-root': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: 0,
                '&:last-child': { paddingBottom: 0 },
              },
              '& .RaShow-aside': {
                position: 'sticky',
                top: 16,
                alignSelf: 'flex-start',
              },
            }
      }
    >
      {isXSmall ? <MobileFuneralShowRedesign /> : <FuneralShowRedesign />}
    </Show>
    </MessageDraftProvider>
  );
};

const MobileFuneralShow = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box sx={{ pb: 10 }}>
      {/* Title */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Cremation for{' '}
            <ReferenceField
              source="deceased_client_id"
              reference="clients"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Typography>
          <RegionChip />
        </Stack>
        <Stack direction="row" alignItems="center">
          <WebChip />
          <CopyAppLinkButton />
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Cremation Tracker */}
      <Box sx={{ mb: 2 }}>
        <CremationTracker />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Client Information Accordion */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Clients</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Primary Client */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Primary Client
                </Typography>
                <ReferenceField
                  source="primary_client_id"
                  reference="clients"
                  link={false}
                >
                  <FunctionField
                    render={(clientRecord: any) => (
                      <Stack spacing={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {clientRecord.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clientRecord.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clientRecord.phone_number}
                        </Typography>
                      </Stack>
                    )}
                  />
                </ReferenceField>
              </CardContent>
            </Card>

            {/* Buyer / Payer */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Buyer / Payer
                </Typography>
                <ReferenceField
                  source="paying_client_id"
                  reference="clients"
                  link={false}
                >
                  <FunctionField
                    render={(clientRecord: any) => (
                      <Stack spacing={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {clientRecord.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clientRecord.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clientRecord.phone_number}
                        </Typography>
                      </Stack>
                    )}
                  />
                </ReferenceField>
              </CardContent>
            </Card>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Contracts Accordion */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6">Contracts</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <FuneralLinkedContractsList />
        </AccordionDetails>
      </Accordion>

      {/* Files Accordion */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachFileIcon color="primary" />
            <Typography variant="h6">Files</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <RecordContextProvider value={record}>
            <FuneralFileUploader />
          </RecordContextProvider>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const FuneralShowAside = () => {
  const record = useRecordContext();
  return (
    <Card style={{ width: 600, marginLeft: '1em' }}>
      <div style={{ padding: '1em' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Primary Client</Typography>
          {record && (
            <ReferenceField
              source="primary_client_id"
              reference="clients"
              link={false}
            >
              <EditInDialogButton fullWidth maxWidth="md" sx={{ padding: 4 }}>
                <SimpleForm toolbar={<ClientSaveToolbar />}>
                  <ClientEditBase />
                </SimpleForm>
              </EditInDialogButton>
            </ReferenceField>
          )}
        </Stack>

        {record && (
          <ReferenceField
            source="primary_client_id"
            reference="clients"
            link={false}
          >
            <FunctionField
              render={(clientRecord: any) => (
                <Stack spacing={1} mt={1}>
                  <Typography>{clientRecord.name}</Typography>
                  <Typography>{clientRecord.email}</Typography>
                  <Stack direction="row" alignItems="center">
                    <Typography>{clientRecord.phone_number}</Typography>
                    <AircallButton phoneNumber={clientRecord.phone_number} />
                  </Stack>
                </Stack>
              )}
            />
          </ReferenceField>
        )}
      </div>
      <div style={{ padding: '1em'}}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Buyer / Payer</Typography>
          {record && (
            <ReferenceField
              source="paying_client_id"
              reference="clients"
              link={false}
            >
              <EditInDialogButton fullWidth maxWidth="md" sx={{ padding: 4 }}>
                <SimpleForm toolbar={<ClientSaveToolbar />}>
                  <ClientEditBase />
                </SimpleForm>
              </EditInDialogButton>
            </ReferenceField>
          )}
        </Stack>
        {record && (
          <ReferenceField
            source="paying_client_id"
            reference="clients"
            link={false}
          >
            <FunctionField
              render={(clientRecord: any) => (
                <Stack spacing={1} mt={1}>
                  <Typography>{clientRecord.name}</Typography>
                  <Typography>{clientRecord.email}</Typography>
                  <Stack direction="row" alignItems="center">
                    <Typography>{clientRecord.phone_number}</Typography>
                    <AircallButton phoneNumber={clientRecord.phone_number} />
                  </Stack>
                </Stack>
              )}
            />
          </ReferenceField>
        )}
      </div>
      <div style={{ padding: '1em' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Authorized Representative</Typography>
          {record && (
            <ReferenceField
              source="authorized_client_id"
              reference="clients"
              link={false}
            >
              <EditInDialogButton fullWidth maxWidth="md" sx={{ padding: 4 }}>
                <SimpleForm toolbar={<ClientSaveToolbar />}>
                  <ClientEditBase />
                </SimpleForm>
              </EditInDialogButton>
            </ReferenceField>
          )}
        </Stack>
        {record && (
          <ReferenceField
            source="authorized_client_id"
            reference="clients"
            link={false}
          >
            <FunctionField
              render={(clientRecord: any) => (
                <Stack spacing={1} mt={1}>
                  <Typography>{clientRecord.name}</Typography>
                  <Typography>{clientRecord.email}</Typography>
                  <Stack direction="row" alignItems="center">
                    <Typography>{clientRecord.phone_number}</Typography>
                    <AircallButton phoneNumber={clientRecord.phone_number} />
                  </Stack>
                </Stack>
              )}
            />
          </ReferenceField>
        )}
      </div>
      <div style={{ padding: '1em' }}>
        <Typography variant="h6">Cremation Partners</Typography>
        <PartnerVisibilityToggle />
        <CremationPartnersDisplay />
      </div>

      <div style={{ padding: '1em' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Delivery Details</Typography>
          {record && (
            <DeliveryEditButton icon={<EditIcon />} />
          )}
        </Stack>
        <DeliveryAddressDisplay />
      </div>

      <div style={{ padding: '1em' }}>
        <Typography variant="h6">Files</Typography>
        {record && (
          <RecordContextProvider value={record}>
            <FuneralFileUploader />
          </RecordContextProvider>
        )}
      </div>
    </Card>
  );
};

const FuneralBaseShow = () => {
  const record = useRecordContext();
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'));
  const translate = useTranslate();

  return record ? (
    <div>
      <FuneralTitle />
      <Box sx={{ width: '100%'}}>
        <CremationTracker />
      </Box>
      <Divider sx={{ mx: 2, mb: 1 }} />
      
      {/* Document Submission Status */}
      <Box sx={{ mx: 2, mb: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Document Status:
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <ValidationStatusIndicator formType="dec" />
            <DecStatusField source="dec_ready" label="DEC" />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" color="text.secondary">
              Benefits
            </Typography>
            <BenefitsStatusField />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <CustomBooleanField source="paperwork1_complete" label="Part 1" />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <CustomBooleanField source="paperwork2_complete" label="Part 2" />
          </Stack>
        </Stack>
      </Box>

      {/* Delivery Plan Status */}
      <Box sx={{ mx: 2, mb: 2 }}>
        <DeliveryPlanStatus />
      </Box>

      {/* Review Request Status */}
      <Box sx={{ mx: 2, mb: 2 }}>
        <ReviewRequestStatus />
      </Box>
      
      <Grid container spacing={2} mb={10}>
        <Grid item xs={10} sm={6}>
          <SimpleShowLayout>
            <ReferenceField
              source="deceased_client_id"
              label="Deceased"
              reference="clients"
              link={false}
            >
              <FunctionField
                render={(record: any) => (
                  <EditInDialogButton 
                    fullWidth 
                    maxWidth="md" 
                    sx={{ padding: 4 }}
                    label={record.name}
                  >
                    <SimpleForm toolbar={<ClientSaveToolbar />}>
                      <ClientEditBase />
                    </SimpleForm>
                  </EditInDialogButton>
                )}
              />
            </ReferenceField>

            {/* <FuneralDateSummary label="Funeral Date" /> */}
            {/* <ReferenceField
              source="chapel_id"
              label="Chapel"
              reference="chapels"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField> */}
            {/* <OtherLocationField label="Location" /> */}
            {/* 
            <ReferenceField
              source="cemetery_id"
              label="Cemetery"
              reference="places"
              link={false}
            >
              <ReferenceField
                source="parent_id"
                reference="places"
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </ReferenceField>

            <ReferenceField
              source="cemetery_id"
              label="Section"
              reference="places"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField> */}

            {/* <TextField source="plot_location" />
            <TextField source="verified_plot" />

            <ReferenceField
              source="family_room_id"
              label="Family Room"
              reference="family_rooms"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField> */}
            <TextField source="special_instructions" />
            <TextField source="internal_notes" />
            <Labeled label="SP3 Data">
              <FunctionField
                render={(record: Funeral) => (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {record.sp3_data ? (
                      <SP3DataViewer sp3Data={record.sp3_data} />
                    ) : (
                      <Typography color="textSecondary">No SP3 data available</Typography>
                    )}
                    <SP3PdfParser />
                  </Box>
                )}
              />
            </Labeled>
          </SimpleShowLayout>
        </Grid>

        <Grid item xs={10} sm={6}>
          <SimpleShowLayout>
            {/* <ReferenceField
              source="deceased_client_id"
              reference="clients"
              label="Hebrew Name"
            >
              <TextField source="hebrew_name" />
            </ReferenceField> */}
            <SpouseField label="Spouse" />

            {/* <ReferenceManyToManyField
              reference="clergy"
              through="funeral_clergy"
              using="funeral_id,clergy_id"
              label="Clergy"
            >
              <SingleFieldList sx={{ marginTop: 0 }} linkType={false}>
                <TextField sx={{ marginRight: 1 }} source="name" />
              </SingleFieldList>
            </ReferenceManyToManyField>
            <TextField source="verified_clergy" />
            <TextField source="casket_note" />
            <FuneralCasket label="Casket Price" />
            <TextField source="shroud" />
            <TextField source="chevra" />
            <TextField source="talit" /> */}
            <TextField source="donations" />
            <ObitField label="Obituary" />
            {record.legacy_clergy ? (
              <TextField source="legacy_clergy" label="Clergy (Import)" />
            ) : null}
          </SimpleShowLayout>
        </Grid>

        <Grid item xs={10} sm={6}>
          <SimpleShowLayout>
            <ReferenceField
              source="deceased_client_id"
              reference="clients"
              label="Date of Birth"
            >
              <DateField source="birthdate" options={{ timeZone: 'UTC' }} />
            </ReferenceField>
            {record?.date_time_of_death &&
              !isNaN(new Date(record.date_time_of_death).getTime()) && (
                <DateField
                  source="date_time_of_death"
                  options={{
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hourCycle: 'h23',
                  }}
                />
              )}
            {/* <TextField source="yahrzeit_date" /> */}
            {/* <HebrewDate label="Yahrzeit Date" /> */}
            {/* <BooleanField source="death_after_sunset" /> */}
            <TextField source="place_of_death" />
            <TextField source="place_of_death_code" label="Place of Death Code" />
            <FunctionField
              label="Place of Death Address"
              render={(record: any) => {
                if (!record.place_of_death_address) return '-';
                
                // If it's a JSON object, format it nicely
                if (typeof record.place_of_death_address === 'object') {
                  const addr = record.place_of_death_address;
                  const parts = [];
                  if (addr.adresse) parts.push(addr.adresse);
                  if (addr.municipalite) parts.push(addr.municipalite);
                  if (addr.province) parts.push(addr.province);
                  if (addr.codePostal) parts.push(addr.codePostal);
                  if (addr.pays) parts.push(addr.pays);
                  return parts.join(', ') || '-';
                }
                
                // Otherwise display as string
                return record.place_of_death_address;
              }}
            />
            <ReferenceField
              source="cause_of_death_id"
              label="Cause of Death"
              reference="cause_of_deaths"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <TextField source="doctor_name" />

            <TextField source="sp3_number" label="SP/3 Number" />
            {/* <TextField source="burial_certificate_number" label="Burial Certificate No."/> */}
            <NumberField
              source="number_death_certificates"
              label="Number Death Certificates"
            />
            <DateField
              source="death_certificate_date"
              options={{ timeZone: 'UTC' }}
            />

            {/* Remove the previous transport and crematorium fields */}

            {/* <BooleanField source="paperman_books" /> */}
            {/* <TextField source="other_books" /> */}
            {/* <BooleanField source="yarmulkes" />
            <BooleanField source="ometz" /> */}
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={10} sm={6}>
          <SimpleShowLayout>
            {/* Existing fields */}
            <BooleanField source="firearms_permit" />
            {/* Stored as 'yes' / 'no' / 'unknown' text, so a BooleanField
                showed a tick for "no" too. */}
            <TextField source="contractor_license" label="RBQ contractor licence" />
            <TextField source="contractor_license_number" label="RBQ licence number" emptyText="—" />
            <BooleanField source="public_sector_pension" />
            <BooleanField source="disability_benefits" />
            <BooleanField source="public_curator" />
            <BooleanField source="coroner" />
            {/* <BooleanField source="shell" label="Shell" /> */}
          </SimpleShowLayout>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Box p={2}>
            {/* <FuneralLinkedClientsList /> */}
            <FuneralLinkedContractsList />
          </Box>
        </Grid>
      </Grid>
    </div>
  ) : null;
};

const SpouseField = (props: TextFieldProps) => {
  const record = useRecordContext();

  return (
    <ReferenceField source="deceased_client_id" reference="clients">
      <FunctionField
        render={(record: any) => {
          const spouseName = record.spouse_id ? (
            <ReferenceField source="spouse_id" reference="clients" link={false}>
              <FunctionField
                render={(spouseRecord: any) => (
                  <EditInDialogButton 
                    fullWidth 
                    maxWidth="md" 
                    sx={{ padding: 4 }}
                    label={spouseRecord.name}
                  >
                    <SimpleForm toolbar={<ClientSaveToolbar />}>
                      <ClientEditBase />
                    </SimpleForm>
                  </EditInDialogButton>
                )}
              />
            </ReferenceField>
          ) : null;

          const legacySpouse = record.legacy_spouse
            ? ` ${record.legacy_spouse}`
            : '';
          return (
            <>
              {spouseName}
              {legacySpouse}
            </>
          );
        }}
      />
    </ReferenceField>
  );
};

const ObitField = (props: TextFieldProps) => {
  const record = useRecordContext();
  if (record.obituary_en?.length > 0) {
    return <Typography>Yes</Typography>;
  } else {
    return <Typography>No</Typography>;
  }
};

const OtherLocationField = (props: TextFieldProps) => {
  const record = useRecordContext();
  return record && record.chapel_id == 4 ? (
    <TextField source="other_location" label="Other Location" />
  ) : null;
};

const FuneralTitle = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" m={2}>
        <Box sx={{ display: 'flex' }}>
          <Stack direction="column">
            <Stack direction="row" alignItems="center">
              <Typography variant="h5">
                <span>Cremation for </span>
                <ReferenceField
                  source="deceased_client_id"
                  reference="clients"
                  link={false}
                >
                  <TextField source="name" variant="h5" />
                </ReferenceField>
              </Typography>
              <WebChip />
              <CopyAppLinkButton />
            </Stack>
          </Stack>
        </Box>
        <RegionChip />
      </Stack>
      <Divider sx={{ mx: 2, mb: 1 }} />
    </>
  );
  // return <span>Funeral for {record.name}</span>;
};

const FuneralDateTitle = () => {
  const record = useRecordContext();
  const mTime = moment(record.funeral_time, ['HH:mm:ssZ']);

  // if (record.funeral_time == null) {
  return (
    <DateField
      source="funeral_date"
      variant="h6"
      showTime
      options={
        {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        } //, hour: 'numeric', minute: 'numeric' }
      }
    />
  );
  // }
  // else{
  //   return (
  //     <DateField source="funeral_date" variant='h6' showTime options={
  //       { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }//, hour: 'numeric', minute: 'numeric' }
  //     } />
  //   );
  // return(
  //   <Stack direction="row">
  //     <DateField source="funeral_date" variant='h6' showTime options={
  //     { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }//, hour: 'numeric', minute: 'numeric' }
  //   } />
  //   <Typography variant='h6'>, {mTime.format('h:mm A')}</Typography>

  //   </Stack>
  // )

  // }
};

const FuneralDateSummary = (props: TextFieldProps) => {
  const record = useRecordContext();
  const mTime = moment(record.funeral_time, ['HH:mm:ssZ']);

  // if (record.funeral_time == null) {
  return (
    <DateField
      source="funeral_date"
      options={{
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }}
    />
  );
};

const WebChip = () => {
  const record = useRecordContext<Funeral>();

  let text = 'Not Live on Website';
  let color = '#ebebeb';

  if (record.is_live) {
    text = 'Live on Website';
    color = '#fef5e4';
  }

  return (
    <Chip label={text} sx={{ marginLeft: '10px', backgroundColor: color }} />
  );
};

const NonPapermanChip = () => {
  const record = useRecordContext<Funeral>();

  if (record.non_paperman_service) {
    const text = 'Non Paperman Funeral';
    const color = '#ffeaba';

    return (
      <Chip label={text} sx={{ marginLeft: '10px', backgroundColor: color }} />
    );
  }

  return null;
};

const CremationPartnersDisplay = () => {
  const record = useRecordContext();
  
  if (!record || (!record.transport_partner_id && !record.crematorium_partner_id)) {
    return <Typography>Not Set</Typography>;
  }
  
  if (!record.transport_partner_id || !record.crematorium_partner_id) {
    return (
      <>
        {record.transport_partner_id && (
          <ReferenceField
            source="transport_partner_id"
            reference="partners"
            label="Transport Company"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        
        {record.crematorium_partner_id && (
          <ReferenceField
            source="crematorium_partner_id"
            reference="partners"
            label="Crematorium"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        )}
      </>
    );
  }
  
  return (
    <Stack spacing={1}>
      <FunctionField
        render={() => (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReferenceField
              source="transport_partner_id"
              reference="partners"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <Typography> → </Typography>
            <ReferenceField
              source="crematorium_partner_id"
              reference="partners"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Stack>
        )}
      />
    </Stack>
  );
};

const DeliveryAddressDisplay = () => {
  const record = useRecordContext();
  
  const hasDeliveryInfo = record?.delivery_date || record?.delivery_window || record?.delivery_note || record?.delivery_address;
  
  if (!record || !hasDeliveryInfo) {
    return <Typography color="textSecondary" sx={{ mt: 1 }}>Not Set</Typography>;
  }
  
  const delivery = record.delivery_address;
  
  // Format the full address
  const formatAddress = () => {
    if (!delivery) return 'Not set';
    const parts = [];
    
    // Format street address (apartment-civic_number address format)
    const streetParts = [];
    if (delivery.apartment && delivery.civic_number) {
      streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
    } else if (delivery.apartment) {
      streetParts.push(delivery.apartment);
    } else if (delivery.civic_number) {
      streetParts.push(delivery.civic_number);
    }
    if (delivery.address) {
      streetParts.push(delivery.address);
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(' '));
    }
    
    if (delivery.city) parts.push(delivery.city);
    if (delivery.province) parts.push(delivery.province);
    if (delivery.postal_code) parts.push(delivery.postal_code);
    return parts.join(', ') || 'Not set';
  };
  
  const getSelectionLabel = () => {
    if (!delivery?.selection) return null;
    switch (delivery.selection) {
      case 'primary': return 'Primary Client';
      case 'authorized': return 'Authorized Representative';
      case 'payer': return 'Payer';
      case 'custom': return 'Custom Address';
      default: return delivery.selection;
    }
  };
  
  const getDeliveryMethodLabel = (method?: string) => {
    switch (method) {
      case 'hand_delivery': return 'Hand Delivery';
      case 'shipping': return 'Shipping (Canada Post)';
      case 'pickup': return 'Customer Pickup';
      default: return null;
    }
  };

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {record.delivery_method && (
        <Typography variant="body2">
          <strong>Delivery Method:</strong> {getDeliveryMethodLabel(record.delivery_method)}
        </Typography>
      )}
      {record.delivery_date && (
        <Typography variant="body2">
          <strong>Delivery Date:</strong>{' '}
          {moment(record.delivery_date).format('MMMM D, YYYY')}
        </Typography>
      )}
      {record.delivery_window && (
        <Typography variant="body2">
          <strong>Delivery Window:</strong> {record.delivery_window}
        </Typography>
      )}
      {record.delivery_note && (
        <Typography variant="body2">
          <strong>Delivery Note:</strong> {record.delivery_note}
        </Typography>
      )}
      {delivery && (
        <>
          {delivery.recipient_name && (
            <Typography variant="body2">
              <strong>Recipient:</strong> {delivery.recipient_name}
            </Typography>
          )}
          {getSelectionLabel() && (
            <Typography variant="body2">
              <strong>Selection:</strong> {getSelectionLabel()}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Address:</strong> {formatAddress()}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default FuneralShow;
